import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@material-ui/core';

import {
  listHinovaCooperativas,
  listHinovaRegionais,
  listHinovaTiposVeiculo,
  listHinovaVeiculos,
  listHinovaSituacoes,
  listSimovsatOrganizations,
  importVehiclesFromHinova,
  isSimovsatAvailable,
} from '~/services/api';
import { handleApiErrorResponse } from '~/services/handleErrors';
import { HinovaCooperativa, HinovaRegional, HinovaTipoVeiculo, HinovaVeiculo, HinovaSituacao, HinovaFilterPresetFilters } from '~/types';
import { Table } from '~/components/Table';
import InputComponent from '~/components/InputComponent';
import Button from '~/components/Button';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';
import { CustomAutocomplete } from '~/components/CustomAutocomplete';
import FilterPresetManager from './FilterPresetManager';

interface Organization {
  id: string;
  email: string;
  company_name: string;
  social_identity?: string;
}

interface ImportFilters {
  situacao: string[];
  tipoVeiculo: string[];
  cooperativa: string[];
  regional: string[];
  regiao: string[];
  valorFipeMin: string;
  valorFipeMax: string;
  ano: string;
  dataInicial: string;
  dataFinal: string;
}

const initialFilters: ImportFilters = {
  situacao: [],
  tipoVeiculo: [],
  cooperativa: [],
  regional: [],
  regiao: [],
  valorFipeMin: '',
  valorFipeMax: '',
  ano: '',
  dataInicial: '',
  dataFinal: '',
};

export default function Importacao() {
  const [filters, setFilters] = useState<ImportFilters>(initialFilters);
  const [loading, setLoading] = useState(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [cooperativas, setCooperativas] = useState<HinovaCooperativa[]>([]);
  const [regionais, setRegionais] = useState<HinovaRegional[]>([]);
  const [tiposVeiculo, setTiposVeiculo] = useState<HinovaTipoVeiculo[]>([]);
  const [situacoes, setSituacoes] = useState<HinovaSituacao[]>([]);

  const [vehicles, setVehicles] = useState<HinovaVeiculo[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<HinovaVeiculo[]>([]);
  const [showTable, setShowTable] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  const [openImportModal, setOpenImportModal] = useState(false);
  const [openSingleImportModal, setOpenSingleImportModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<HinovaVeiculo | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [singleImportLoading, setSingleImportLoading] = useState(false);
  const [importFormData, setImportFormData] = useState({
    organization_id: '',
    km_min: '0',
    km_max: '999999',
  });
  const [singleImportFormData, setSingleImportFormData] = useState({
    organization_id: '',
    km_min: '0',
    km_max: '999999',
  });

  const loadFilters = useCallback(async () => {
    setLoading(true);
    const failedSources: string[] = [];
    let lastError: ReturnType<typeof handleApiErrorResponse> | null = null;

    const setDataFromResponse = (response: any, setter: (data: any[]) => void) => {
      if (response?.status === 200 && response?.data != null) {
        const data = response.data;
        setter(Array.isArray(data) ? data : data.data || []);
        return true;
      }
      return false;
    };

    try {
      const [cooperativasResponse, regionaisResponse, tiposVeiculoResponse, situacoesResponse] = await Promise.allSettled([
        listHinovaCooperativas(),
        listHinovaRegionais(),
        listHinovaTiposVeiculo('ativo'),
        listHinovaSituacoes(),
      ]);

      if (cooperativasResponse.status === 'fulfilled') {
        if (!setDataFromResponse(cooperativasResponse.value, setCooperativas)) {
          failedSources.push('Cooperativas');
        }
      } else {
        failedSources.push('Cooperativas');
        lastError = handleApiErrorResponse(cooperativasResponse.reason);
      }

      if (regionaisResponse.status === 'fulfilled') {
        if (!setDataFromResponse(regionaisResponse.value, setRegionais)) {
          failedSources.push('Regionais');
        }
      } else {
        failedSources.push('Regionais');
        if (!lastError) lastError = handleApiErrorResponse(regionaisResponse.reason);
      }

      if (tiposVeiculoResponse.status === 'fulfilled') {
        if (!setDataFromResponse(tiposVeiculoResponse.value, setTiposVeiculo)) {
          failedSources.push('Tipos de Veículo');
        }
      } else {
        failedSources.push('Tipos de Veículo');
        if (!lastError) lastError = handleApiErrorResponse(tiposVeiculoResponse.reason);
      }

      if (situacoesResponse.status === 'fulfilled') {
        setDataFromResponse(situacoesResponse.value, setSituacoes);
      }

      // Mostra erro se alguma fonte essencial falhar
      if (failedSources.length > 0) {
        const detail =
          failedSources.length === 3
            ? 'Não foi possível carregar filtros de importação.'
            : `Não foi possível carregar: ${failedSources.join(', ')}.`;
        setSeverity('warning');
        setMessageSnack(
          (lastError?.applicationErrorMessage || lastError?.apiError || 'Ocorreu um erro') + ': ' + detail,
        );
        setOpenSnackbar(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setFilters(initialFilters);
    setShowTable(false);
    setFilteredVehicles([]);
    setVehicles([]);
    loadFilters();
  }, [loadFilters]);

  const loadOrganizations = useCallback(async () => {
    if (!isSimovsatAvailable()) return;
    try {
      setLoadingOrganizations(true);
      const response = await listSimovsatOrganizations(1, 100);
      if (response.status === 200 && response.data?.data) {
        setOrganizations(response.data.data);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível carregar organizações.',
      );
      setOpenSnackbar(true);
    } finally {
      setLoadingOrganizations(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const parseHinovaDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    // Handle dd/mm/yyyy returned by Hinova
    if (dateStr.includes('/')) {
      const [d, m, y] = dateStr.split('/');
      const parsed = new Date(`${y}-${m}-${d}`);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleFilter = useCallback(async () => {
    if (!filters.situacao || filters.situacao.length === 0) {
      setSeverity('warning');
      setMessageSnack('Selecione pelo menos uma situação antes de filtrar.');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoadingVehicles(true);

      // Fetch vehicles for each selected situação in parallel and merge results
      const responses = await Promise.all(
        filters.situacao.map(situacaoCode => listHinovaVeiculos(situacaoCode)),
      );

      const mergedMap = new Map<string, HinovaVeiculo>();
      for (const response of responses) {
        if (response.status === 200 && response.data) {
          const data = response.data;
          const batch: HinovaVeiculo[] = Array.isArray(data) ? data : data.veiculos || [];
          for (const vehicle of batch) {
            const key = vehicle.placa || vehicle.chassi || JSON.stringify(vehicle);
            if (!mergedMap.has(key)) {
              mergedMap.set(key, vehicle);
            }
          }
        }
      }

      const allVehicles = Array.from(mergedMap.values());
      setVehicles(allVehicles);

      const dataInicialDate = filters.dataInicial ? new Date(filters.dataInicial) : null;
      const dataFinalDate = filters.dataFinal ? new Date(filters.dataFinal) : null;

      const filtered = allVehicles.filter((vehicle: HinovaVeiculo) => {
        if (filters.tipoVeiculo.length > 0) {
          const vehicleCodigoTipo =
            (vehicle as any).codigo_tipo_veiculo?.toString() ||
            (vehicle as any).codigo_tipo?.toString() ||
            '';
          if (!filters.tipoVeiculo.includes(vehicleCodigoTipo)) {
            return false;
          }
        }
        if (filters.ano) {
          const anoFilter = Number(filters.ano);
          const vehicleAno = (vehicle as any).ano_fabricacao || (vehicle as any).ano_modelo || vehicle.ano;
          if (vehicleAno !== anoFilter) {
            return false;
          }
        }
        if ((filters.valorFipeMin || filters.valorFipeMax) && vehicle.valor_fipe != null) {
          if (filters.valorFipeMin && vehicle.valor_fipe < Number(filters.valorFipeMin)) {
            return false;
          }
          if (filters.valorFipeMax && vehicle.valor_fipe > Number(filters.valorFipeMax)) {
            return false;
          }
        }
        if (filters.cooperativa.length > 0) {
          const vehicleCooperativa =
            (vehicle as any).codigo_cooperativa?.toString() || vehicle.cooperativa?.toString() || '';
          const validCooperativas = filters.cooperativa.filter(c => !c.startsWith('coop-'));
          if (validCooperativas.length > 0 && !validCooperativas.includes(vehicleCooperativa)) {
            return false;
          }
        }
        if (filters.regional.length > 0) {
          const vehicleRegional =
            (vehicle as any).codigo_regional?.toString() || vehicle.regional?.toString() || '';
          const validRegionais = filters.regional.filter(r => !r.startsWith('regional-'));
          if (validRegionais.length > 0 && !validRegionais.includes(vehicleRegional)) {
            return false;
          }
        }
        if (filters.regiao.length > 0) {
          const vehicleRegiao = vehicle.regiao || (vehicle as any).nome_regiao || '';
          if (vehicleRegiao && !filters.regiao.includes(vehicleRegiao)) {
            return false;
          }
        }
        if (dataInicialDate || dataFinalDate) {
          const rawDataContrato =
            (vehicle as any).data_contrato || (vehicle as any).data_vigencia || '';
          const vehicleDate = rawDataContrato ? parseHinovaDate(rawDataContrato) : null;
          if (vehicleDate) {
            if (dataInicialDate && vehicleDate < dataInicialDate) return false;
            if (dataFinalDate && vehicleDate > dataFinalDate) return false;
          }
        }
        return true;
      });

      setFilteredVehicles(filtered);
      setShowTable(true);
      setSeverity('success');
      setMessageSnack(`${filtered.length} veículo(s) encontrado(s).`);
      setOpenSnackbar(true);
    } catch (err) {
      const error = handleApiErrorResponse(err);
      const statusCode =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      const is404 = statusCode === 404;

      setSeverity('warning');
      if (is404) {
        setMessageSnack(
          'O endpoint para listar veículos não está disponível na API Hinova. A funcionalidade de filtragem requer um endpoint que retorne a lista completa de veículos.',
        );
      } else {
        setMessageSnack(
          (error?.applicationErrorMessage || error?.apiError || 'Erro desconhecido') +
            ': não foi possível buscar veículos. Tente novamente.',
        );
      }
      setOpenSnackbar(true);
      setShowTable(false);
      setFilteredVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  }, [filters]);

  const handleChange = (field: 'valorFipeMin' | 'valorFipeMax' | 'ano' | 'dataInicial' | 'dataFinal') => (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const value = (event.target.value as string) || '';
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleLoadPreset = useCallback((presetFilters: HinovaFilterPresetFilters) => {
    setFilters(presetFilters);
    setSeverity('info');
    setMessageSnack('Filtro carregado. Ajuste se necessário e clique em Filtrar.');
    setOpenSnackbar(true);
  }, []);

  const hasAtLeastOneFilter = useMemo(() => {
    return (
      filters.situacao.length > 0 ||
      filters.tipoVeiculo.length > 0 ||
      filters.cooperativa.length > 0 ||
      filters.regional.length > 0 ||
      !!filters.valorFipeMin ||
      !!filters.valorFipeMax ||
      !!filters.ano ||
      filters.regiao.length > 0 ||
      !!filters.dataInicial ||
      !!filters.dataFinal
    );
  }, [filters]);

  const handleOpenImportModal = useCallback(() => {
    if (!hasAtLeastOneFilter) {
      setSeverity('warning');
      setMessageSnack('Selecione pelo menos um filtro antes de importar.');
      setOpenSnackbar(true);
      return;
    }
    setImportFormData({ organization_id: '', km_min: '', km_max: '' });
    setOpenImportModal(true);
  }, [hasAtLeastOneFilter]);

  const handleCloseImportModal = useCallback(() => {
    setOpenImportModal(false);
    setImportFormData({ organization_id: '', km_min: '0', km_max: '999999' });
  }, []);

  const handleMassImport = useCallback(async () => {
    if (!importFormData.organization_id) {
      setSeverity('warning');
      setMessageSnack('Selecione uma organização de destino.');
      setOpenSnackbar(true);
      return;
    }
    
    const kmMin = importFormData.km_min.trim() 
      ? Number(importFormData.km_min) 
      : 0;
    const kmMax = importFormData.km_max.trim() 
      ? Number(importFormData.km_max) 
      : 999999;
    
    if (Number.isNaN(kmMin) || Number.isNaN(kmMax) || kmMin >= kmMax) {
      setSeverity('warning');
      setMessageSnack('KM mínimo deve ser menor que KM máximo.');
      setOpenSnackbar(true);
      return;
    }

    try {
      setImportLoading(true);
      const cooperativaIds = filters.cooperativa
        .filter(c => !c.startsWith('coop-'))
        .map(c => Number(c))
        .filter(n => !Number.isNaN(n));
      const cooperativaId = cooperativaIds[0] ?? 0;
      const payload = {
        filters: {
          id: cooperativaId,
          km_min: kmMin,
          km_max: kmMax,
          placa: undefined,
          status: 'ativo',
          user_id: 0,
          cooperativas: filters.cooperativa.filter(c => !c.startsWith('coop-')),
          regionais: filters.regional.filter(r => !r.startsWith('regional-')),
          situacoes: filters.situacao,
          tiposVeiculo: filters.tipoVeiculo,
          regioes: filters.regiao,
        },
        organization_id: importFormData.organization_id,
      };
      const response = await importVehiclesFromHinova(payload);

      // O endpoint retorna 202 quando a importação é iniciada em background
      if (response.status === 200 || response.status === 201 || response.status === 202) {
        setSeverity('success');
        setMessageSnack(
          'Processo de importação iniciado com sucesso! Os veículos serão processados em background.',
        );
        setOpenSnackbar(true);
        handleCloseImportModal();
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('error');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError || 'Erro desconhecido') +
          ': não foi possível iniciar a importação. Tente novamente.',
      );
      setOpenSnackbar(true);
    } finally {
      setImportLoading(false);
    }
  }, [importFormData, filters, handleCloseImportModal]);

  const handleOpenSingleImportModal = useCallback((vehicle: HinovaVeiculo) => {
    setSelectedVehicle(vehicle);
    setOpenSingleImportModal(true);
    if (organizations.length === 0) {
      loadOrganizations();
    }
  }, [organizations.length, loadOrganizations]);

  const handleCloseSingleImportModal = useCallback(() => {
    setOpenSingleImportModal(false);
    setSelectedVehicle(null);
    setSingleImportFormData({
      organization_id: '',
      km_min: '0',
      km_max: '999999',
    });
  }, []);

  const handleSingleImport = useCallback(async () => {
    if (!selectedVehicle) return;

    if (!singleImportFormData.organization_id.trim()) {
      setSeverity('warning');
      setMessageSnack('Selecione uma organização de destino.');
      setOpenSnackbar(true);
      return;
    }
    
    const kmMin = singleImportFormData.km_min.trim() 
      ? Number(singleImportFormData.km_min) 
      : 0;
    const kmMax = singleImportFormData.km_max.trim() 
      ? Number(singleImportFormData.km_max) 
      : 999999;
    
    if (Number.isNaN(kmMin) || Number.isNaN(kmMax) || kmMin >= kmMax) {
      setSeverity('warning');
      setMessageSnack('KM mínimo deve ser menor que KM máximo.');
      setOpenSnackbar(true);
      return;
    }

    try {
      setSingleImportLoading(true);
      const payload = {
        filters: {
          id: 0,
          km_min: kmMin,
          km_max: kmMax,
          placa: selectedVehicle.placa,
          status: 'ativo',
          user_id: 0,
        },
        organization_id: singleImportFormData.organization_id,
      };
      const response = await importVehiclesFromHinova(payload);

      // O endpoint retorna 202 quando a importação é iniciada em background
      if (response.status === 200 || response.status === 201 || response.status === 202) {
        setSeverity('success');
        setMessageSnack(
          `Veículo ${selectedVehicle.placa} importado com sucesso! O processo será concluído em background.`,
        );
        setOpenSnackbar(true);
        handleCloseSingleImportModal();
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('error');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError || 'Erro desconhecido') +
          ': não foi possível importar o veículo. Tente novamente.',
      );
      setOpenSnackbar(true);
    } finally {
      setSingleImportLoading(false);
    }
  }, [selectedVehicle, singleImportFormData, handleCloseSingleImportModal]);

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: 20 }} alignItems='flex-end'>
        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            multiple
            options={situacoes}
            getOptionLabel={(option) => {
              const s = option as HinovaSituacao;
              const codigo = s.codigo_situacao?.toString() || s.codigo?.toString() || '';
              const descricao = s.descricao_situacao || s.descricao || s.situacao || codigo;
              return descricao;
            }}
            getOptionSelected={(option, value) => {
              const codigoOpt = (option as HinovaSituacao).codigo_situacao?.toString() || (option as HinovaSituacao).codigo?.toString() || '';
              const codigoVal = (value as HinovaSituacao).codigo_situacao?.toString() || (value as HinovaSituacao).codigo?.toString() || '';
              return codigoOpt === codigoVal;
            }}
            value={situacoes.filter(s => {
              const codigo = s.codigo_situacao?.toString() || s.codigo?.toString() || '';
              return filters.situacao.includes(codigo);
            })}
            onChange={(_, newValue) => {
              const codigos = (newValue as HinovaSituacao[]).map(s => s.codigo_situacao?.toString() || s.codigo?.toString() || '');
              setFilters(prev => ({ ...prev, situacao: codigos }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Situação *" required variant="outlined" />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomAutocomplete
            multiple
            options={tiposVeiculo}
            getOptionLabel={(option) => (option as HinovaTipoVeiculo).descricao_tipo || ''}
            getOptionSelected={(option, value) =>
              (option as HinovaTipoVeiculo).codigo_tipo === (value as HinovaTipoVeiculo).codigo_tipo
            }
            value={tiposVeiculo.filter(t => filters.tipoVeiculo.includes(String(t.codigo_tipo ?? '')))}
            onChange={(_, newValue) => {
              const codigos = (newValue as HinovaTipoVeiculo[]).map(t => String(t.codigo_tipo ?? ''));
              setFilters(prev => ({ ...prev, tipoVeiculo: codigos }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Tipo de veículo" variant="outlined" />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <CustomAutocomplete
            multiple
            options={cooperativas}
            getOptionLabel={(option) => (option as HinovaCooperativa).nome ?? (option as any).name ?? ''}
            getOptionSelected={(option, value) => {
              const getKey = (coop: HinovaCooperativa, idx: number) => {
                const codigo = coop.codigo ?? coop.id ?? (coop as any).codigo_cooperativa;
                return codigo != null && codigo !== '' ? String(codigo) : `coop-${idx}`;
              };
              const idxO = cooperativas.indexOf(option as HinovaCooperativa);
              const idxV = cooperativas.indexOf(value as HinovaCooperativa);
              return getKey(option as HinovaCooperativa, idxO) === getKey(value as HinovaCooperativa, idxV);
            }}
            value={cooperativas.filter((c, idx) => {
              const codigo = c.codigo ?? c.id ?? (c as any).codigo_cooperativa;
              const key = codigo != null && codigo !== '' ? String(codigo) : `coop-${idx}`;
              return filters.cooperativa.includes(key);
            })}
            onChange={(_, newValue) => {
              const keys = (newValue as HinovaCooperativa[]).map(c => {
                const codigo = c.codigo ?? c.id ?? (c as any).codigo_cooperativa;
                const idx = cooperativas.indexOf(c);
                return codigo != null && codigo !== '' ? String(codigo) : (idx >= 0 ? `coop-${idx}` : '');
              }).filter(Boolean);
              setFilters(prev => ({ ...prev, cooperativa: keys }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Cooperativa" variant="outlined" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomAutocomplete
            multiple
            options={regionais}
            getOptionLabel={(option) => (option as HinovaRegional).nome ?? (option as any).name ?? ''}
            getOptionSelected={(option, value) => {
              const getKey = (reg: HinovaRegional, idx: number) => {
                const codigo = reg.codigo ?? reg.id ?? (reg as any).codigo_regional;
                return codigo != null && codigo !== '' ? String(codigo) : `regional-${idx}`;
              };
              const idxO = regionais.indexOf(option as HinovaRegional);
              const idxV = regionais.indexOf(value as HinovaRegional);
              return getKey(option as HinovaRegional, idxO) === getKey(value as HinovaRegional, idxV);
            }}
            value={regionais.filter((r, idx) => {
              const codigo = r.codigo ?? r.id ?? (r as any).codigo_regional;
              const key = codigo != null && codigo !== '' ? String(codigo) : `regional-${idx}`;
              return filters.regional.includes(key);
            })}
            onChange={(_, newValue) => {
              const keys = (newValue as HinovaRegional[]).map(reg => {
                const codigo = reg.codigo ?? reg.id ?? (reg as any).codigo_regional;
                const idx = regionais.indexOf(reg);
                return codigo != null && codigo !== '' ? String(codigo) : (idx >= 0 ? `regional-${idx}` : '');
              }).filter(Boolean);
              setFilters(prev => ({ ...prev, regional: keys }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Regional" variant="outlined" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <InputComponent
            label='Valor Fipe mínimo'
            name='valorFipeMin'
            type='number'
            value={filters.valorFipeMin}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, valorFipeMin: value }));
            }}
            error={null}
            placeholder='Ex: 10000'
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <InputComponent
            label='Valor Fipe máximo'
            name='valorFipeMax'
            type='number'
            value={filters.valorFipeMax}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, valorFipeMax: value }));
            }}
            error={null}
            placeholder='Ex: 50000'
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InputComponent
            label='Ano'
            name='ano'
            type='number'
            value={filters.ano}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, ano: value }));
            }}
            error={null}
            placeholder='Ano'
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <CustomAutocomplete
            multiple
            options={regionais}
            getOptionLabel={(option) => (option as HinovaRegional).nome ?? (option as any).name ?? ''}
            getOptionSelected={(option, value) => {
              const nomeO = (option as HinovaRegional).nome ?? (option as any).name ?? '';
              const nomeV = (value as HinovaRegional).nome ?? (value as any).name ?? '';
              return nomeO === nomeV;
            }}
            value={regionais.filter(r => {
              const nome = r.nome ?? (r as any).name ?? '';
              return nome ? filters.regiao.includes(nome) : false;
            })}
            onChange={(_, newValue) => {
              const nomes = (newValue as HinovaRegional[]).map(r => r.nome ?? (r as any).name ?? '').filter(Boolean);
              setFilters(prev => ({ ...prev, regiao: nomes }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Região" variant="outlined" fullWidth />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InputComponent
            label='Data inicial'
            name='dataInicial'
            type='date'
            value={filters.dataInicial}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, dataInicial: value }));
            }}
            error={null}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <InputComponent
            label='Data final'
            name='dataFinal'
            type='date'
            value={filters.dataFinal}
            onChange={e => {
              const value = e?.target?.value ?? '';
              setFilters(prev => ({ ...prev, dataFinal: value }));
            }}
            error={null}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 12,
              width: '100%',
            }}
          >
            <Button
              label='Filtrar'
              loading={loadingVehicles}
              onClick={handleFilter}
              filled
              style={{ flex: 1, minWidth: 0, marginTop: 0 }}
            />
            <FilterPresetManager
              currentFilters={filters as HinovaFilterPresetFilters}
              onLoadPreset={handleLoadPreset}
              onSnack={(message, sev) => {
                setMessageSnack(message);
                setSeverity(sev);
                setOpenSnackbar(true);
              }}
              hasAtLeastOneFilter={hasAtLeastOneFilter}
              buttonStyle={{ flex: 1, minWidth: 0, marginTop: 0 }}
            />
            <Button
              label="Limpar filtros"
              onClick={() => setFilters(initialFilters)}
              filled
              style={{ flex: 1, minWidth: 0, marginTop: 0 }}
            />
          </div>
        </Grid>
      </Grid>

      {showTable && (
        <Grid container spacing={2} style={{ marginTop: 20, marginBottom: 20 }}>
          <Grid item xs={12}>
            <Table<HinovaVeiculo>
              isLoading={loadingVehicles}
              data={filteredVehicles}
              columns={{
                placa: { title: 'Placa', width: 100 },
                modelo: { title: 'Modelo', width: 150 },
                marca: { title: 'Marca', width: 120 },
                ano: { title: 'Ano', width: 80, type: 'numeric' },
                cor: { title: 'Cor', width: 100 },
                tipo_veiculo: { title: 'Tipo', width: 120 },
                valor_fipe: {
                  title: 'Valor FIPE',
                  width: 120,
                  render: rowData => {
                    if (!rowData.valor_fipe) return '-';
                    return new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(rowData.valor_fipe);
                  },
                },
                chassi: { title: 'Chassi', width: 150 },
                renavam: { title: 'RENAVAM', width: 120 },
                acoes: {
                  title: 'Ações',
                  width: 150,
                  render: rowData => (
                    <Button
                      label='Importação única'
                      onClick={() => handleOpenSingleImportModal(rowData)}
                      filled
                      style={{ minWidth: 140, backgroundColor: '#00F0FF' }}
                    />
                  ),
                },
              }}
              localization={{
                body: {
                  emptyDataSourceMessage: 'Nenhum veículo encontrado com os filtros selecionados.',
                },
                pagination: {
                  labelRowsSelect: 'veículos',
                },
              }}
              options={{
                showTitle: false,
                pageSize: 10,
                pageSizeOptions: [10, 20, 50],
                paging: true,
                filtering: false,
                search: false,
                toolbar: true,
                exportButton: true,
                exportAllData: true,
              }}
            />
          </Grid>
        </Grid>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <Button
          label='Importação em massa'
          filled
          loading={importLoading}
          onClick={handleOpenImportModal}
          disabled={!hasAtLeastOneFilter}
        />
      </div>

      <Dialog open={openImportModal} onClose={handleCloseImportModal} maxWidth='sm' fullWidth>
        <DialogTitle>Importação em massa</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={loadingOrganizations}>
                <InputLabel>Organização de destino</InputLabel>
                <Select
                  value={importFormData.organization_id}
                  onChange={e => {
                    const value = (e?.target?.value ?? '') as string;
                    setImportFormData(prev => ({ ...prev, organization_id: value }));
                  }}
                >
                  {organizations.map(org => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.company_name || org.email || org.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <InputComponent
                label='KM mínimo (opcional)'
                name='km_min'
                type='number'
                value={importFormData.km_min}
                onChange={e => {
                  const value = e?.target?.value ?? '';
                  setImportFormData(prev => ({ ...prev, km_min: value }));
                }}
                error={null}
                placeholder='Padrão: 0'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputComponent
                label='KM máximo (opcional)'
                name='km_max'
                type='number'
                value={importFormData.km_max}
                onChange={e => {
                  const value = e?.target?.value ?? '';
                  setImportFormData(prev => ({ ...prev, km_max: value }));
                }}
                error={null}
                placeholder='Padrão: 999999'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button label='Cancelar' onClick={handleCloseImportModal} disabled={importLoading} />
          <Button
            label='Confirmar importação'
            filled
            loading={importLoading}
            onClick={handleMassImport}
          />
        </DialogActions>
      </Dialog>

      <Dialog open={openSingleImportModal} onClose={handleCloseSingleImportModal} maxWidth='sm' fullWidth>
        <DialogTitle>
          Importação única - {selectedVehicle?.placa ? `Placa: ${selectedVehicle.placa}` : 'Veículo'}
        </DialogTitle>
        <DialogContent>
          {selectedVehicle && (
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12}>
                <p style={{ margin: '8px 0', fontSize: '14px', color: '#666' }}>
                  <strong>Modelo:</strong> {selectedVehicle.modelo || '-'} | <strong>Marca:</strong>{' '}
                  {selectedVehicle.marca || '-'} | <strong>Ano:</strong> {selectedVehicle.ano || '-'}
                </p>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={loadingOrganizations}>
                  <InputLabel>Organização de destino</InputLabel>
                  <Select
                    value={singleImportFormData.organization_id}
                    onChange={e => {
                      const value = (e?.target?.value ?? '') as string;
                      setSingleImportFormData(prev => ({ ...prev, organization_id: value }));
                    }}
                  >
                    {organizations.map(org => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.company_name || org.email || org.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <InputComponent
                  label='KM mínimo (opcional)'
                  name='km_min'
                  type='number'
                  value={singleImportFormData.km_min}
                  onChange={e => {
                    const value = e?.target?.value ?? '';
                    setSingleImportFormData(prev => ({ ...prev, km_min: value }));
                  }}
                  error={null}
                  placeholder='Padrão: 0'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputComponent
                  label='KM máximo (opcional)'
                  name='km_max'
                  type='number'
                  value={singleImportFormData.km_max}
                  onChange={e => {
                    const value = e?.target?.value ?? '';
                    setSingleImportFormData(prev => ({ ...prev, km_max: value }));
                  }}
                  error={null}
                  placeholder='Padrão: 999999'
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button label='Cancelar' onClick={handleCloseSingleImportModal} disabled={singleImportLoading} />
          <Button
            label='Confirmar importação'
            filled
            loading={singleImportLoading}
            onClick={handleSingleImport}
          />
        </DialogActions>
      </Dialog>

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </>
  );
}
